import {FormField} from '@components/system/FormField/FormField';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip
} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import './AddStatementDialog.scss';
import GridTable, {
  DEFAULT_GRID_TABLE__OPTIONS
} from '@components/system/GridTable/GridTable';
import {MTableToolbar} from 'material-table';
import TertiaryButton from '@components/system/ButtonField/TertiaryButton';
import {
  APP,
  EDITABLE_GRID_BORDER_COLOR,
  FONT_PRIMARY_COLOR,
  MESSAGE_GRID_HEADER_BACKGROUND_COLOR
} from 'src/style/variable';
import {CSSProperties} from '@material-ui/core/styles/withStyles';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import CheckIcon from '@material-ui/icons/Check';
import HelperUtils from '@shared/utils/HelperUtils';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import {useSelector} from 'react-redux';
import {State} from '@shared/store/reducers/Index';
import {
  Controller,
  FieldError,
  FieldErrors,
  SubmitErrorHandler,
  SubmitHandler,
  useForm
} from 'react-hook-form';
import moment from 'moment';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {
  createInvoice,
  updateInvoice
} from '@shared/services/DueInvoiceServices';
import {getClinicDetails} from '@shared/services/ClinicServices';

interface IAddStatementDialogProps {
  open: boolean;
  onClose: (val: 'cancel' | 'confirm') => void;
  groupedInvoiceLines: any;
  patient: any;
  defaultClinicId: number;
  clinicIdsWithUnpaidStatement: number[];
}
/* eslint-disable camelcase */
interface IInvoiceLineFields {
  date_of_service: string;
  description: string;
  amount: number;
}

interface IAddStatementFields {
  clinic_id: number;
  statement_date: string;
  date_of_service: string;
  invoice_number: string;
  invoice_date: string;
  total_due: number;
  invoice_lines: IInvoiceLineFields[];
}

type IInvoiceLineGridRow = Partial<IInvoiceLineFields> & {rowIdx: number};
/* eslint-enable camelcase */

const AddStatementDialog = (
  props: IAddStatementDialogProps
): React.ReactElement => {
  const {
    open,
    onClose,
    groupedInvoiceLines,
    patient,
    defaultClinicId,
    clinicIdsWithUnpaidStatement
  } = props;
  const {t} = useTranslation();
  const {groupId, userGroups} = useSelector((state: any) => state.auth);

  let selectedGroup: any;
  if (userGroups && Array.isArray(userGroups) && userGroups.length > 0) {
    selectedGroup = userGroups.find(item => item.id == groupId);
  }

  const {errors, control, handleSubmit, watch, setValue} =
    useForm<IAddStatementFields>({
      mode: 'onChange',
      defaultValues: {
        invoice_lines: [],
        clinic_id: defaultClinicId
      },
      resolver: values => {
        const validationErrors: FieldErrors<IAddStatementFields> = {};
        if (!values.clinic_id) {
          validationErrors.clinic_id = {
            type: 'required',
            message: t('ADD_STATEMENTS.CLINIC_REQUIRED_ERROR_TEXT')
          };
        }
        if (!values.statement_date) {
          validationErrors.statement_date = {
            type: 'required',
            message: t('ADD_STATEMENTS.STATEMENT_DATE_REQUIRED_ERROR_TEXT')
          };
        }
        if (!values.date_of_service) {
          validationErrors.date_of_service = {
            type: 'required',
            message: t('ADD_STATEMENTS.DATE_OF_SERVICE_REQUIRED_ERROR_TEXT')
          };
        } else if (!moment(values.date_of_service).isValid()) {
          validationErrors.date_of_service = {
            type: 'required',
            message: t('VALIDATORS.INVALID_DATE')
          };
        }
        if (!values.invoice_number) {
          validationErrors.invoice_number = {
            type: 'required',
            message: t('ADD_STATEMENTS.INVOICE_NUMBER_REQUIRED_ERROR_TEXT')
          };
        }
        if (!values.invoice_date) {
          validationErrors.invoice_date = {
            type: 'required',
            message: t('ADD_STATEMENTS.INVOICE_DATE_REQUIRED_ERROR_TEXT')
          };
        } else if (!moment(values.invoice_date).isValid()) {
          validationErrors.invoice_date = {
            type: 'required',
            message: t('VALIDATORS.INVALID_DATE')
          };
        }
        if (isNaN(parseFloat(values.total_due as any))) {
          validationErrors.total_due = {
            type: 'required',
            message: t('ADD_STATEMENTS.TOTAL_DUE_REQUIRED_ERROR_TEXT')
          };
        }
        // At least one invoice line is required
        if (
          !Array.isArray(values.invoice_lines) ||
          values.invoice_lines.length === 0
        ) {
          validationErrors.invoice_lines = {
            // @ts-ignore
            type: 'required',
            message: t('ADD_STATEMENTS.ONE_INVOICE_LINE_REQUIRED_ERROR_TEXT')
          };
        } else {
          for (const line of values.invoice_lines) {
            if (isNaN(parseFloat(line.amount as any))) {
              validationErrors.invoice_lines = {
                // @ts-ignore
                type: 'required',
                message: t(
                  'ADD_STATEMENTS.INVOICE_LINE_AMOUNT_REQUIRED_ERROR_TEXT'
                )
              };
            } else if (!line.date_of_service) {
              validationErrors.invoice_lines = {
                // @ts-ignore
                type: 'required',
                message: t(
                  'ADD_STATEMENTS.INVOICE_LINE_SERVICE_DATE_REQUIRED_ERROR_TEXT'
                )
              };
            } else if (!moment(line.date_of_service).isValid()) {
              validationErrors.invoice_lines = {
                // @ts-ignore
                type: 'required',
                message: t(
                  'ADD_STATEMENTS.INVALID_INVOICE_LINE_SERVICE_DATE_ERROR_TEXT'
                )
              };
            } else if (!line.description) {
              validationErrors.invoice_lines = {
                // @ts-ignore
                type: 'required',
                message: t(
                  'ADD_STATEMENTS.INVOICE_LINE_DESCRIPTION_REQUIRED_ERROR_TEXT'
                )
              };
            }

            if (validationErrors.invoice_lines) {
              break;
            }
          }
        }
        if (Object.keys(validationErrors).length) {
          return {
            errors: validationErrors,
            values: {}
          };
        }
        return {
          values,
          errors: {}
        };
      }
    });

  const {clinic_id: selectedClinicId} = watch(['clinic_id']);

  const updateStatementFields = () => {
    if (!selectedClinicId) return;
    const clinicsInvoiceData = groupedInvoiceLines?.[selectedClinicId];
    let statementDate: null | string = null;
    if (
      typeof clinicsInvoiceData?.due?.statement_month === 'number' &&
      typeof clinicsInvoiceData?.due?.statement_year === 'number'
    ) {
      statementDate = HelperUtils.formatDisplayDate(
        moment()
          .month(clinicsInvoiceData?.due?.statement_month - 1)
          .year(clinicsInvoiceData?.due?.statement_year)
          .toDate(),
        'MM/YYYY'
      );
    } else {
      statementDate = HelperUtils.formatDisplayDate(
        moment().toDate(),
        'MM/YYYY'
      );
    }
    const statementFieldValues: Partial<IAddStatementFields> = {
      total_due: clinicsInvoiceData?.due?.original_due_amount ?? '',
      date_of_service: clinicsInvoiceData?.invoice?.visited_on
        ? HelperUtils.formatDisplayDate(
            moment(clinicsInvoiceData?.invoice?.visited_on)
          )
        : (null as any),
      invoice_date: clinicsInvoiceData?.invoice?.invoice_date
        ? HelperUtils.formatDisplayDate(
            clinicsInvoiceData?.invoice?.invoice_date
          )
        : (null as any),
      invoice_number: clinicsInvoiceData?.invoice?.invoice_number ?? '',
      statement_date: statementDate as any
    };
    setTimeout(() => {
      Object.keys(statementFieldValues).forEach(key => {
        setValue(key, statementFieldValues[key as 'date_of_service']);
      });
    }, 0);
  };

  // ROW INDEX MUST BE THERE
  const [rows, setRows] = useState<IInvoiceLineGridRow[]>([]);

  const [editableRowIds, setEditableRowIds] = useState<number[]>([]);

  const updateFormFieldsDataWithClinicsData = () => {
    if (!selectedClinicId) return;
    const clinicsInvoiceData = groupedInvoiceLines?.[selectedClinicId];
    updateStatementFields();
    setEditableRowIds([]);
    if (
      Array.isArray(clinicsInvoiceData?.invoice?.lines) &&
      clinicsInvoiceData.invoice.lines.length > 0
    ) {
      const clinicInvoiceLines: IInvoiceLineFields[] =
        clinicsInvoiceData.invoice.lines.map(
          (iLine: any): IInvoiceLineFields => {
            const invoiceLineAmount = parseFloat(iLine.amount);
            return {
              amount: isNaN(invoiceLineAmount)
                ? (null as any as number)
                : invoiceLineAmount,
              description: iLine.description,
              date_of_service: iLine.service_date
                ? HelperUtils.formatDisplayDate(iLine.service_date)
                : (null as any)
            };
          }
        );
      setValue('invoice_lines', clinicInvoiceLines);
      setRows(
        clinicInvoiceLines.map((item, idx) => ({...item, rowIdx: idx + 1}))
      );
    } else {
      setRows([]);
      setValue('invoice_lines', []);
    }
  };

  useEffect(() => {
    updateFormFieldsDataWithClinicsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClinicId, groupedInvoiceLines, setValue]);

  useEffect(() => {
    const updatedInvoiceLines: Partial<IInvoiceLineFields>[] = rows
      .map((rowItem): Partial<IInvoiceLineFields> => {
        return {
          date_of_service: rowItem.date_of_service,
          description: rowItem.description,
          amount: rowItem.amount
        };
      })
      .filter(rowItem => Object.values(rowItem).some(item => item != null));

    // update the invoice_lines
    setValue('invoice_lines', updatedInvoiceLines);

    // update the total amount too
    if (updatedInvoiceLines.length > 0) {
      const totalDue = updatedInvoiceLines.reduce((total, invoiceLine) => {
        if (!invoiceLine.amount) return total;
        const amountVal = parseFloat(invoiceLine.amount as any);
        if (isNaN(amountVal)) return total;
        return total + amountVal;
      }, 0);
      setValue('total_due', totalDue);
    } else {
      setValue('total_due', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const commonHeaderStyle = {
    fontFamily: APP.FONT_FAMILY.MEDIUM,
    width: '10%',
    backgroundColor: MESSAGE_GRID_HEADER_BACKGROUND_COLOR,
    color: FONT_PRIMARY_COLOR,
    fontSize: '0.875rem',
    padding: '0.7rem',
    height: '2rem',
    whiteSpace: 'normal',
    border: `1px solid ${EDITABLE_GRID_BORDER_COLOR}`,
    textAlign: 'left'
  };

  const commonCellStyle = {
    fontSize: '0.875rem',
    padding: '0.7rem',
    border: `1px solid ${EDITABLE_GRID_BORDER_COLOR}`
  };

  const statementFields = [
    {
      label: t('ADD_STATEMENTS.DATE_OF_SERVICE_LABEL'),
      formKey: 'date_of_service',
      type: 'date',
      placeholder: t('ADD_STATEMENTS.DATE_OF_SERVICE_PLACEHOLDER'),
      defaultValue: null
    },
    {
      label: t('ADD_STATEMENTS.INVOICE_NUMBER_LABEL'),
      formKey: 'invoice_number',
      type: 'number',
      placeholder: t('ADD_STATEMENTS.INVOICE_NUMBER_PLACEHOLDER'),
      defaultValue: ''
    },
    {
      label: t('ADD_STATEMENTS.INVOICE_DATE_LABEL'),
      formKey: 'invoice_date',
      type: 'date',
      placeholder: t('ADD_STATEMENTS.INVOICE_DATE_PLACEHOLDER'),
      defaultValue: null
    },
    {
      label: t('ADD_STATEMENTS.TOTAL_DUE_LABEL'),
      formKey: 'total_due',
      type: 'currency',
      placeholder: t('ADD_STATEMENTS.TOTAL_DUE_PLACEHOLDER'),
      defaultValue: ''
    }
  ];

  const addInvoiceLine = () => {
    const newRowData = {rowIdx: rows.length + 1};
    if (rows.length > 0) {
      newRowData.rowIdx = rows[rows.length - 1].rowIdx + 1;
    }
    setRows([...rows, newRowData]);
    setEditableRowIds(prevEditableRowId => [
      ...prevEditableRowId,
      newRowData.rowIdx
    ]);
  };

  const onEditRow = (rowIdx: number) => {
    setEditableRowIds(prevEditableRowId => [...prevEditableRowId, rowIdx]);
  };

  const onSaveRow = (rowIdx: number) => {
    setEditableRowIds(prevEditableRowId =>
      prevEditableRowId.filter(i => i != rowIdx)
    );
  };

  const onDeleteRow = (rowIdx: number) => {
    const updatedRows = rows.filter(i => i.rowIdx !== rowIdx);
    setRows(updatedRows);
  };

  const handleGridInputChange = ({
    rowIdx,
    field,
    value
  }: {
    rowIdx: number;
    field: string;
    value: any;
  }) => {
    const updatedRowIndex = rows.findIndex(row => row.rowIdx === rowIdx);
    if (updatedRowIndex < 0) return;

    const updatedRow = {...rows[updatedRowIndex], [field]: value};

    const updatedRows = [...rows];
    updatedRows[updatedRowIndex] = updatedRow;

    setRows(updatedRows);
  };
  const clinicsList = useSelector((state: State) => state.clinics.list) ?? [];
  const {enqueueSnackbar} = useSnackbar();

  const addOrUpdateStatements: SubmitHandler<
    IAddStatementFields
  > = async values => {
    if (!values.clinic_id) return;
    if (!patient.patient_id) return;
    const isEditing = !!groupedInvoiceLines?.[values.clinic_id];
    try {
      if (isEditing) {
        await updateInvoice({
          clinic_group_id: groupId,
          clinic_id: values.clinic_id,
          payload: {
            invoice_date: values.invoice_date as string,
            invoice_number: values.invoice_number as string,
            total_due: values.total_due as number,
            date_of_service: values.date_of_service as string,
            invoice_lines: values.invoice_lines as IInvoiceLineFields[],
            statement_month:
              moment(values.statement_date, 'M/YYYY').month() + 1,
            statement_year: moment(values.statement_date, 'M/YYYY').year(),
            patient_id: patient.patient_id
          }
        });
      } else {
        await createInvoice({
          clinic_group_id: groupId,
          clinic_id: values.clinic_id,
          payload: {
            invoice_date: values.invoice_date as string,
            invoice_number: values.invoice_number as string,
            total_due: values.total_due as number,
            date_of_service: values.date_of_service as string,
            invoice_lines: values.invoice_lines as IInvoiceLineFields[],
            statement_month:
              moment(values.statement_date, 'M/YYYY').month() + 1,
            statement_year: moment(values.statement_date, 'M/YYYY').year(),
            patient_id: patient.patient_id
          }
        });
      }
      onClose('confirm');
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };

  const handleValidationError: SubmitErrorHandler<
    IAddStatementFields
  > = formErrors => {
    const errorsObj = Object.values(formErrors) as FieldError[];

    if (errorsObj?.[0]?.message) {
      enqueueSnackbar(errorsObj?.[0]?.message, SnackBarConfig('e'));
    }
  };

  useEffect(() => {
    const updateStatementMonthAndYear = async () => {
      if (!groupId || !selectedClinicId) return;
      try {
        const res = await getClinicDetails({
          clinicId: selectedClinicId,
          groupId,
          fetch_last_imported_month: true
        });
        let statementDate: null | string = null;
        if (
          typeof res?.data?.guarantorDues?.statement_month === 'number' &&
          typeof res?.data?.guarantorDues?.statement_year === 'number'
        ) {
          statementDate = HelperUtils.formatDisplayDate(
            moment()
              .month(res?.data?.guarantorDues?.statement_month - 1)
              .year(res?.data?.guarantorDues?.statement_year)
              .toDate(),
            'MM/YYYY'
          );
        } else {
          statementDate = HelperUtils.formatDisplayDate(
            moment().toDate(),
            'MM/YYYY'
          );
        }
        setValue('statement_date', statementDate);
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      }
    };
    updateStatementMonthAndYear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClinicId, groupId]);

  return (
    <Dialog
      maxWidth={'md'}
      fullWidth={true}
      open={open}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      className="add-statements-dialog">
      <DialogTitle disableTypography>
        <Trans
          i18nKey={'ADD_STATEMENTS.ADD_STATEMENT_TITLE'}
          values={{
            patientName: HelperUtils.commaSeparatedFullName(
              patient?.first_name,
              patient?.last_name,
              patient?.middle_name
            ),
            accountNumber: patient?.patient_account_number
          }}
          components={[<strong key={'X'} />, <strong key={'Y'} />]}
        />
      </DialogTitle>
      <DialogContent dividers>
        <div className="clinic-select-and-statement-date-wrapper">
          <Controller
            control={control}
            name="clinic_id"
            render={inputProps => {
              return (
                <FormField
                  type="select"
                  label={t('ADD_STATEMENTS.PRACTICE_INPUT_LABEL')}
                  disabled={
                    !selectedGroup?.patients_shared ||
                    !selectedGroup?.consolidated_group_billing
                  }
                  placeholder={t('ADD_STATEMENTS.PRACTICE_INPUT_PLACEHOLDER')}
                  itemList={clinicsList
                    .filter((c: any) =>
                      clinicIdsWithUnpaidStatement.includes(c.id)
                    )
                    .map((clinicItem: any) => {
                      return {
                        value: clinicItem?.id,
                        label: clinicItem.name,
                        secondary_label: (
                          <>
                            {clinicItem.emr_provider_detail?.name && (
                              <>
                                <span>
                                  {clinicItem.emr_provider_detail?.name}
                                </span>
                                &nbsp;/&nbsp;
                              </>
                            )}
                            <span>
                              {clinicItem.city}, {clinicItem.country}
                            </span>
                          </>
                        )
                      };
                    })}
                  showSecondaryLabelInValue={false}
                  showRadio={true}
                  {...inputProps}
                  error={errors['clinic_id']}
                />
              );
            }}
          />
          <Controller
            control={control}
            name="statement_date"
            defaultValue={null}
            render={inputProps => {
              return (
                <FormField
                  type="date"
                  format={'MM/yyyy'}
                  label={t('ADD_STATEMENTS.MONTH_AND_YEAR_LABEL')}
                  placeholder={t('ADD_STATEMENTS.MONTH_AND_YEAR_PLACEHOLDER')}
                  {...inputProps}
                  value={
                    inputProps.value
                      ? moment(inputProps.value, 'MM/YYYY').format('MM/DD/YYYY')
                      : inputProps.value
                  }
                  disabled={true}
                  error={errors['statement_date']}
                  views={['month', 'year']}
                />
              );
            }}
          />
        </div>
        <div className="statements-input-fields">
          {statementFields.map(({formKey, ...inputItem}) => {
            return (
              <Controller
                key={formKey}
                control={control}
                name={formKey}
                defaultValue={inputItem.defaultValue}
                render={inputProps => {
                  return (
                    <FormField
                      {...inputItem}
                      {...inputProps}
                      error={errors[formKey as 'clinic_id']}
                    />
                  );
                }}
              />
            );
          })}
        </div>
        <GridTable
          title={
            <p className="grid-title">
              {t('ADD_STATEMENTS.INVOICE_LINE_GRID_HEADER')}
            </p>
          }
          components={{
            Toolbar: toolbarProps => (
              <div className="gird-toolbar-wrapper">
                <MTableToolbar {...toolbarProps} />
                <TertiaryButton
                  style={{fontWeight: 'bold'}}
                  onClick={addInvoiceLine}>
                  {t('COMMON.ADD')}
                </TertiaryButton>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={updateFormFieldsDataWithClinicsData}
                  style={{fontWeight: 'bold'}}>
                  {t('COMMON.RESET_BUTTON_LABEL')}
                </Button>
              </div>
            )
          }}
          localization={{
            body: {
              emptyDataSourceMessage: t('ADD_STATEMENTS.NO_INVOICE_LINES')
            }
          }}
          options={{
            ...DEFAULT_GRID_TABLE__OPTIONS,
            paging: false,
            sorting: false,
            emptyRowsWhenPaging: false,
            showEmptyDataSourceMessage: true,
            maxBodyHeight: '100%',
            // REVERTING DEFAULT OPTION VALUES
            showTitle: true,
            search: false,
            toolbar: true,
            // TODO: WHY DO I NEED IT
            headerStyle: {
              fontFamily: APP.FONT_FAMILY.MEDIUM,
              width: '10%',
              backgroundColor: MESSAGE_GRID_HEADER_BACKGROUND_COLOR,
              fontSize: '0.875rem',
              padding: '0.7rem',
              height: '2rem',
              whiteSpace: 'normal',
              color: FONT_PRIMARY_COLOR
            },
            actionsColumnIndex: -1,
            rowStyle: {
              border: `1px solid ${EDITABLE_GRID_BORDER_COLOR}`
            }
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            boxShadow:
              '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)',
            cursor: 'pointer',
            maxHeight: '100%',
            margin: '0.1rem',
            marginTop: '1rem'
          }}
          columns={[
            {
              title: t('ADD_STATEMENTS.SERVICE_DATE_INPUT_LABEL'),
              editable: 'always',
              headerStyle: {
                ...commonHeaderStyle,
                width: '20%'
              } as CSSProperties,
              cellStyle: {
                ...commonCellStyle,
                width: '20%'
              } as CSSProperties,
              render: (row: IInvoiceLineGridRow) => {
                if (editableRowIds.includes(row.rowIdx)) {
                  return (
                    <FormField
                      type="date"
                      value={row?.date_of_service ?? null}
                      onChange={(value: string) => {
                        handleGridInputChange({
                          field: 'date_of_service',
                          rowIdx: row.rowIdx,
                          value: value
                        });
                      }}
                      style={{minWidth: '5rem'}}
                      placeholder={t(
                        'ADD_STATEMENTS.SERVICE_DATE_INPUT_PLACEHOLDER'
                      )}
                    />
                  );
                }
                return (
                  <p>
                    {HelperUtils.formatDisplayDate(row?.date_of_service) || '-'}
                  </p>
                );
              }
            },
            {
              title: t('ADD_STATEMENTS.DESCRIPTION_INPUT_LABEL'),
              headerStyle: {
                ...commonHeaderStyle,
                width: '40%'
              } as CSSProperties,
              cellStyle: {
                ...commonCellStyle,
                width: '40%'
              } as CSSProperties,
              render: (row: IInvoiceLineGridRow) => {
                if (editableRowIds.includes(row.rowIdx)) {
                  return (
                    <FormField
                      type="text"
                      defaultValue={row?.description}
                      style={{minWidth: '5rem'}}
                      placeholder={t(
                        'ADD_STATEMENTS.DESCRIPTION_INPUT_PLACEHOLDER'
                      )}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        handleGridInputChange({
                          field: 'description',
                          rowIdx: row.rowIdx,
                          value: e.target.value
                        });
                      }}
                    />
                  );
                }
                return row?.description || '-';
              }
            },
            {
              title: t('ADD_STATEMENTS.AMOUNT_INPUT_LABEL'),
              headerStyle: {
                ...commonHeaderStyle,
                width: '20%'
              } as CSSProperties,
              cellStyle: {
                ...commonCellStyle,
                width: '20%'
              } as CSSProperties,
              render: (row: IInvoiceLineGridRow) => {
                if (editableRowIds.includes(row.rowIdx)) {
                  return (
                    <FormField
                      type="currency"
                      style={{minWidth: '5rem'}}
                      placeholder={t('ADD_STATEMENTS.AMOUNT_INPUT_PLACEHOLDER')}
                      defaultValue={row?.amount}
                      onChange={(value: number) => {
                        handleGridInputChange({
                          field: 'amount',
                          rowIdx: row.rowIdx,
                          value: value
                        });
                      }}
                    />
                  );
                }
                if (row.amount === null || row.amount === undefined) {
                  return '-';
                }
                return (
                  <CurrencyFormatter
                    value={row.amount || 0}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={'$'}
                    ignoreRound={true}
                    useSign={true}
                  />
                );
              }
            },
            {
              title: t('CLAIMS_TICKETS.CPT_CODES.ACTION_HEADER_LABEL'),
              field: 'actions',
              headerStyle: {
                ...commonHeaderStyle,
                width: '15%'
              } as CSSProperties,
              cellStyle: {
                ...commonCellStyle,
                width: '15%'
              } as CSSProperties,
              render: (row: any) => {
                if (row.rowIdx === -1) return null;
                return (
                  <div>
                    {editableRowIds.includes(row?.rowIdx) ? (
                      <Tooltip title={t('COMMON.SAVE') as string}>
                        <IconButton
                          onClick={() => onSaveRow(row?.rowIdx)}
                          className="action-icon-button">
                          <CheckIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title={t('COMMON.EDIT') as string}>
                          <IconButton
                            onClick={() => onEditRow(row?.rowIdx)}
                            className="action-icon-button">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={t('COMMON.DELETE') as string}
                          className="action-icon-button"
                          onClick={() => onDeleteRow(row?.rowIdx)}>
                          <IconButton>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </div>
                );
              }
            }
          ]}
          data={rows}
        />
        <Controller
          name="invoice_lines"
          control={control}
          render={() => <input type={'hidden'} />}
        />
      </DialogContent>
      <DialogActions style={{display: 'flex'}}>
        <Button onClick={() => onClose('cancel')}>{t('COMMON.CANCEL')}</Button>
        <Button
          variant={'contained'}
          color="primary"
          onClick={handleSubmit(addOrUpdateStatements, handleValidationError)}>
          {t('COMMON.SUBMIT')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddStatementDialog;
